import type { ApiClient } from '../../api/client';
import { modelChangeApproval, publishAccess } from '../editor/model-change-approval';
import { text } from '../../app/i18n';
import { presetUsages } from '@ayumia/component-runtime';
import type { EffectiveModelAtlas, FieldPresetBody, FormEditProposalsResult, StudioDraftResult, TranslationAvailabilityResult, TranslationSuggestionsResult } from '@ayumia/contracts';
import type { DraftPort, Finding, GovernancePort, ImpactReport, Proposal } from '../editor/editor-types';
import type { ConformityRuleOption } from './forms-editors';
import type { Template } from './forms-model';
import type { TemplateVersionRow } from './form-history';
import type { EditorRegistry, RegistryComponent, RegistryLayout, RegistryRecipe } from './layout-registry';
import type { ModelObject, PresetRow, StudioCatalog } from './v2/library';
import { PRESET_SAVE_UNAVAILABLE, type PresetsPort, type UsageReport } from './v2/PresetDialogs';
import type { AssistantMarks, AssistantPort } from './v2/assistant';
import type { PendingSuggestion, TranslatePort } from './v2/locales';
import { publishRefusal } from './publish-errors';

/** Relecture d'une demande de traduction : toutes les secondes, une minute au plus. */
const TRANSLATION_POLL_MS = 1000;
const TRANSLATION_POLLS = 60;

/**
 * Branchement du profil « formulaire » sur l'API v1 : trois lectures du Studio (`ListInspectionTemplates`,
 * `GetInspectionTemplate`, `ValidateInspectionTemplateDraft`, `GetInspectionTemplateImpact`, côté serveur
 * dans `packages/config-runtime/src/form-studio.ts`) et le circuit `ModelChange` d'ADR-130 étendu aux
 * gabarits (`model-change` 1.1.0, `target_kind: inspection_template` ; contrat demandé au couloir principal).
 * Le navigateur ne nomme jamais le tenant ni l'auteur : la session les porte.
 */

export interface TemplateListItem {
  artifact_id: string;
  label?: Record<string, string>;
  form: string | null;
  origin: string;
  owner_ref?: string;
  artifact_version: string;
  status: string;
  overridden_by?: { artifact_id: string; artifact_version: string; origin: string };
  pending_versions: string[];
  editable: boolean;
}

export interface TemplateDetail {
  artifact_id: string;
  artifact_version: string;
  origin: string;
  status: string;
  form: string | null;
  body: Template;
  overridden_by?: { artifact_id: string; artifact_version: string; origin: string };
  /** Règles de conformité actives (bornes servies), pour choisir un `rule_ref`. */
  rules: ConformityRuleOption[];
}

interface ImpactData {
  artifact_id: string;
  base_version: string;
  origin: string;
  form: string | null;
  related: Array<{ artifact_id: string; artifact_version: string; origin: string }>;
  workflows: Array<{ artifact_id: string; artifact_version: string }>;
  open_work_items: Array<{ artifact_id: string; artifact_version: string; open: number }>;
  open_total: number;
  change?: { structural: boolean; changes: Array<{ path: string; kind: 'label' | 'structure' }> };
}

interface ModelChangeRow {
  id: string;
  reference?: string;
  status: 'proposed' | 'published' | 'rejected';
  structural: boolean;
  reason: string;
  proposed_by?: string;
  decision_note?: string;
  published_artifact_version?: string;
}

/** Même document JSON, ordre des clés ignoré. */
function sameJson(left: unknown, right: unknown): boolean {
  const stable = (value: unknown): unknown =>
    Array.isArray(value) ? value.map(stable) : value !== null && typeof value === 'object' ? Object.fromEntries(Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([key, item]) => [key, stable(item)])) : value;
  return JSON.stringify(stable(left)) === JSON.stringify(stable(right));
}

export const ORIGIN_LABELS: Readonly<Record<string, string>> = { core: 'Core', pack: 'Pack', customer: 'Client', solution: 'Solution' };

export class FormsStudioApi {
  constructor(private readonly client: ApiClient) {}

  async list(): Promise<TemplateListItem[]> {
    return (await this.client.query<TemplateListItem[]>('ListInspectionTemplates')).data;
  }

  async get(artifactId: string): Promise<TemplateDetail> {
    return (await this.client.query<TemplateDetail>('GetInspectionTemplate', { artifact_id: artifactId })).data;
  }

  async validate(body: Template): Promise<{ valid: boolean; findings: Finding[] }> {
    return (await this.client.queryPost<{ valid: boolean; findings: Finding[] }>('ValidateInspectionTemplateDraft', { body: JSON.stringify(body) })).data;
  }

  async impact(artifactId: string, body: Template): Promise<ImpactData> {
    return (await this.client.queryPost<ImpactData>('GetInspectionTemplateImpact', { artifact_id: artifactId, body: JSON.stringify(body) })).data;
  }

  /** Registres de l'éditeur 1 (ADR-146 T3) : gabarits de mise en page, recettes (jamais affichées), blocs. */
  async registry(): Promise<EditorRegistry> {
    const [layouts, components] = await Promise.all([
      this.client.query<{ layouts: RegistryLayout[]; recipes: RegistryRecipe[] }>('ListLayoutRegistry'),
      this.client.query<{ components: RegistryComponent[] }>('ListComponentRegistry'),
    ]);
    return { layouts: layouts.data.layouts, recipes: layouts.data.recipes, components: components.data.components };
  }

  /**
   * Bibliothèque de l'éditeur v2 : champs du modèle effectif (`GetEffectiveModel`), préréglages actifs
   * (`ListFieldPresets`) et noms des couches. Chaque lecture peut manquer (droit absent, requête pas encore
   * servie) : l'onglet concerné reste vide, l'éditeur s'ouvre quand même.
   */
  async catalog(): Promise<StudioCatalog> {
    const [model, presets] = await Promise.allSettled([
      this.client.query<EffectiveModelAtlas>('GetEffectiveModel'),
      this.client.query<{ presets: PresetRow[] }>('ListFieldPresets'),
    ]);
    const atlas = model.status === 'fulfilled' ? model.value.data : undefined;
    const layers: Record<string, string> = {};
    for (const layer of atlas?.layers ?? []) if (layer.label !== undefined) layers[layer.lineage] = text(layer.label, 'fr', layer.lineage);
    const objects: ModelObject[] = (atlas?.objects ?? [])
      .filter((object) => object.inheritance_state !== 'masked' && object.inheritance_state !== 'removed')
      .map((object) => ({
        object_type: object.object_type,
        label: object.label as Record<string, string>,
        ...(object.lineage === undefined ? {} : { lineage: object.lineage }),
        fields: object.fields
          .filter((field) => field.inheritance_state !== 'masked' && field.inheritance_state !== 'removed')
          .map((field) => ({
            field_id: field.field_id,
            label: field.label as Record<string, string>,
            type: field.type,
            origin: field.origin,
            ...(field.unit === undefined ? {} : { unit: field.unit }),
            ...(field.reference_type === undefined ? {} : { reference_type: field.reference_type }),
            ...(field.enum_values === undefined ? {} : { enum_values: field.enum_values }),
            ...(field.lineage === undefined ? {} : { lineage: field.lineage }),
          })),
      }));
    return {
      model: objects,
      presets: presets.status === 'fulfilled' ? presets.value.data.presets : [],
      layers,
      ...(model.status === 'rejected' ? { modelUnavailable: true } : {}),
      ...(presets.status === 'rejected' ? { presetsUnavailable: true } : {}),
    };
  }

  /**
   * Usages d'un préréglage : `ListFieldPresetUsages` au serveur ; si la requête n'est pas servie, le poste les
   * calcule par le même calcul du Core (`presetUsages`) sur les gabarits actifs qu'il peut lire, et le dit.
   */
  presets(catalog: () => StudioCatalog): PresetsPort {
    return {
      saveUnavailable: PRESET_SAVE_UNAVAILABLE,
      // `model-change` 1.2.0, `target_kind: field_preset` : la proposition recopie le réglage dans chaque gabarit qui
      // le cite ; elle est publiée d'un geste si la politique du tenant le permet à l'auteur, sinon elle attend.
      save: async (body, presetId) => {
        const current = presetId === undefined ? undefined : catalog().presets.find((row) => row.artifact_id === presetId);
        if (presetId !== undefined && current === undefined) throw new Error('Ce préréglage n’est plus dans la bibliothèque : rechargez l’éditeur.');
        const name = text(body.label as Record<string, string> | undefined, 'fr', presetId ?? 'Préréglage');
        const reason = current === undefined ? `Nouveau préréglage « ${name} »` : `Préréglage « ${name} » modifié`;
        const result = await this.client.command<{ result?: { model_change_id?: string } }>('ProposeModelChange', {
          payload: { target_kind: 'field_preset', ...(current === undefined ? {} : { target_artifact_id: current.artifact_id, base_artifact_version: current.artifact_version }), changes: [{ kind: 'replace_preset_body', body }], reason },
        });
        const id = result.result?.model_change_id;
        if (id === undefined) throw new Error('La proposition n’a pas rendu son identifiant.');
        if ((await modelChangeApproval(this.client, id)).can) await this.client.command('ApproveModelChange', { payload: { note: reason }, target: { type: 'ModelChange', id } });
      },
      usages: async (presetId, proposed) => {
        try {
          const response = await this.client.queryPost<Omit<UsageReport, 'computedBy'>>('ListFieldPresetUsages', { preset_id: presetId, ...(proposed === undefined ? {} : { proposed_body: JSON.stringify(proposed) }) });
          return { ...response.data, computedBy: 'server' };
        } catch {
          const current = catalog().presets.find((row) => row.artifact_id === presetId);
          const listed = (await this.list()).filter((item) => item.status === 'published' && item.overridden_by === undefined);
          const templates = await Promise.all(listed.map(async (item) => ({ artifact_id: item.artifact_id, artifact_version: item.artifact_version, status: 'active' as const, body: (await this.get(item.artifact_id)).body })));
          const usages = presetUsages(templates, presetId, proposed === undefined || current === undefined ? undefined : { current: current.body, proposed: proposed as FieldPresetBody });
          return {
            preset_version: current?.artifact_version ?? '',
            usages,
            counts: { forms: new Set(usages.map((usage) => usage.template_id)).size },
            truncated: false,
            computedBy: 'client',
          };
        }
      },
    };
  }

  /**
   * Traduction suggérée (ADR-146, addendum du 2026-10-22) : `GetTranslationAvailability` dit si elle est servie ;
   * `SuggestTranslations` démarre la demande au Work Engine, `GetTranslationSuggestions` la relit jusqu'à la
   * réponse. Le poste ne connaît aucun fournisseur.
   */
  translations(detail: TemplateDetail): TranslatePort & { availability(): Promise<TranslationAvailabilityResult> } {
    return {
      availability: async () => (await this.client.query<TranslationAvailabilityResult>('GetTranslationAvailability', {})).data,
      suggest: async (texts, sourceLocale, locales) => {
        const started = await this.client.command<{ result?: { request_id?: string } }>('SuggestTranslations', {
          payload: { target_kind: 'inspection_template', target_artifact_id: detail.artifact_id, source_locale: sourceLocale, target_locales: [...locales], texts: [...texts] },
        });
        const id = started.result?.request_id;
        if (id === undefined) throw new Error('La demande n’a pas rendu son identifiant.');
        for (let poll = 0; poll < TRANSLATION_POLLS; poll += 1) {
          const read = (await this.client.query<TranslationSuggestionsResult>('GetTranslationSuggestions', { request_id: id })).data;
          if (read.status === 'ready') return { suggestions: (read.suggestions ?? []) as PendingSuggestion[], missing: read.missing ?? [] };
          if (read.status === 'failed') throw new Error(read.failure?.message ?? 'Traduction impossible.');
          await new Promise((resolve) => setTimeout(resolve, TRANSLATION_POLL_MS));
        }
        throw new Error('La traduction prend trop de temps : réessayez.');
      },
    };
  }

  async versions(artifactId: string): Promise<TemplateVersionRow[]> {
    return (await this.client.query<{ versions: TemplateVersionRow[] }>('ListInspectionTemplateVersions', { artifact_id: artifactId })).data.versions;
  }

  async version(artifactId: string, artifactVersion: string): Promise<Template> {
    return (await this.client.query<{ body: Template }>('GetInspectionTemplateVersion', { artifact_id: artifactId, artifact_version: artifactVersion })).data.body;
  }

  /** Brouillon de l'auteur au serveur (GetStudioDraft), ou `null`. */
  async draft(artifactId: string): Promise<StudioDraftResult['draft']> {
    return (await this.client.query<StudioDraftResult>('GetStudioDraft', { target_kind: 'inspection_template', target_artifact_id: artifactId })).data.draft;
  }

  /**
   * Port du brouillon au serveur : SaveStudioDraft (concurrence optimiste) et DiscardStudioDraft. Les marques
   * des opérations de l'assistant encore présentes voyagent avec chaque enregistrement (studio-draft 1.1.0).
   */
  drafts(detail: TemplateDetail, marks?: AssistantMarks): DraftPort<Template> {
    const target = { target_kind: 'inspection_template', target_artifact_id: detail.artifact_id };
    return {
      save: async (document, expectedVersion) => {
        const assistantEdits = marks?.current(document) ?? [];
        const result = await this.client.command<{ result?: { draft_version?: number; saved_at?: string } }>('SaveStudioDraft', {
          payload: { ...target, base_artifact_version: detail.artifact_version, body: document, ...(assistantEdits.length === 0 ? {} : { assistant_edits: assistantEdits }) },
          ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }),
        });
        if (result.result?.draft_version === undefined || result.result.saved_at === undefined) throw new Error('L’enregistrement n’a pas rendu sa version.');
        return { version: result.result.draft_version, savedAt: result.result.saved_at };
      },
      discard: async () => {
        await this.client.command('DiscardStudioDraft', { payload: target });
      },
    };
  }

  /** Assistant de l'éditeur (ADR-146, addendum du 2026-10-22) : demande sur le brouillon serveur, proposition relue. */
  assistant(detail: TemplateDetail): AssistantPort {
    return {
      draft: async () => {
        const draft = await this.draft(detail.artifact_id);
        return draft === null || draft.draft_id === undefined ? null : { draft_id: String(draft.draft_id), draft_version: draft.draft_version, body: draft.body as unknown as Template };
      },
      propose: async (payload) => {
        const result = await this.client.command<{ result?: { proposal_id?: string } }>('ProposeFormEdits', { payload: { target_kind: 'inspection_template', target_artifact_id: detail.artifact_id, ...payload } });
        const id = result.result?.proposal_id;
        if (id === undefined) throw new Error('La demande n’a pas rendu son identifiant.');
        return id;
      },
      proposal: async (proposalId) => (await this.client.query<FormEditProposalsResult>('GetFormEditProposals', { proposal_id: proposalId })).data,
    };
  }

  /** Port de gouvernance du socle pour un gabarit : la cible est l'artefact édité (Pack : surcharge client créée à la publication). */
  governance(detail: TemplateDetail, me: string | undefined): GovernancePort<Template> {
    const toProposal = (row: ModelChangeRow): Proposal => ({
      id: row.id,
      ...(row.reference === undefined ? {} : { reference: row.reference }),
      status: row.status,
      structural: row.structural,
      proposedByMe: me !== undefined && row.proposed_by === me,
      reason: row.reason,
      ...(row.decision_note === undefined ? {} : { decisionNote: row.decision_note }),
      ...(row.published_artifact_version === undefined ? {} : { publishedVersion: row.published_artifact_version }),
    });
    const reload = async (id: string): Promise<Proposal> => toProposal((await this.client.getObject<ModelChangeRow>('ModelChange', id)).data);
    return {
      validate: (draft) => this.validate(draft),
      impact: async (draft) => impactReport(await this.impact(detail.artifact_id, draft)),
      pending: async () => {
        const rows = (await this.client.listObjects<ModelChangeRow>('ModelChange', { filter: { target_artifact_id: { eq: detail.artifact_id } }, sort: [{ field: 'proposed_at', direction: 'desc' }], limit: 1 })).data;
        return rows[0] === undefined ? null : toProposal(rows[0]);
      },
      propose: async (draft, reason) => {
        const result = await this.client
          .command<{ result?: { model_change_id?: string } }>('ProposeModelChange', {
            payload: { target_kind: 'inspection_template', target_artifact_id: detail.artifact_id, base_artifact_version: detail.artifact_version, changes: [{ kind: 'replace_template_body', body: draft }], reason },
          })
          .catch((failure: unknown) => {
            throw publishRefusal(failure, draft);
          });
        const id = result.result?.model_change_id;
        if (id === undefined) throw new Error('La proposition n’a pas rendu son identifiant.');
        return reload(id);
      },
      approve: async (proposal, note) => {
        await this.client.command('ApproveModelChange', { payload: note === undefined ? {} : { note }, target: { type: 'ModelChange', id: proposal.id } });
        return reload(proposal.id);
      },
      canApprove: (proposal) => modelChangeApproval(this.client, proposal.id),
      publishAccess: (change) => publishAccess(this.client, 'inspection_template', change.structural),
      // Publier en une action quand la politique le permet : la proposition, puis l'approbation par l'auteur.
      // Un essai précédent arrêté entre les deux (vérification par code demandée, réseau) a laissé sa proposition
      // en attente : si elle porte ce même brouillon, on l'approuve au lieu d'en proposer une seconde.
      publish: async (draft, reason) => {
        const pending = (await this.client.listObjects<ModelChangeRow & { changes?: Array<{ body?: unknown }> }>('ModelChange', { filter: { target_artifact_id: { eq: detail.artifact_id }, status: { eq: 'proposed' } }, limit: 1 })).data[0];
        const resumed = pending !== undefined && sameJson(pending.changes?.[0]?.body, draft) ? pending.id : undefined;
        const result = resumed !== undefined
          ? { result: { model_change_id: resumed } }
          : await this.client
              .command<{ result?: { model_change_id?: string } }>('ProposeModelChange', {
                payload: { target_kind: 'inspection_template', target_artifact_id: detail.artifact_id, base_artifact_version: detail.artifact_version, changes: [{ kind: 'replace_template_body', body: draft }], reason },
              })
              .catch((failure: unknown) => {
                throw publishRefusal(failure, draft);
              });
        const id = result.result?.model_change_id;
        if (id === undefined) throw new Error('La publication n’a pas rendu son identifiant.');
        await this.client.command('ApproveModelChange', { payload: { note: reason }, target: { type: 'ModelChange', id } });
        return reload(id);
      },
      reject: async (proposal, reason) => {
        await this.client.command('RejectModelChange', { payload: { reason }, target: { type: 'ModelChange', id: proposal.id } });
        return reload(proposal.id);
      },
    };
  }
}

/** Rapport d'impact du socle, à partir de la réponse du serveur. */
export function impactReport(data: ImpactData): ImpactReport {
  const open = data.open_work_items.filter((row) => row.open > 0);
  const report: ImpactReport = {
    sections: [
      { title: 'Workflows qui affichent ce formulaire', empty: 'Aucun workflow actif ne le résout.', items: data.workflows.map((workflow) => ({ label: workflow.artifact_id, detail: `version ${workflow.artifact_version}` })) },
      { title: 'Tâches en cours', empty: 'Aucune tâche ouverte sur ce formulaire.', items: open.map((row) => ({ label: `${row.open} tâche(s) sur ${row.artifact_id}`, detail: `elles gardent la version ${row.artifact_version}` })) },
      { title: 'Versions du même formulaire', empty: 'Aucune autre couche.', items: data.related.filter((row) => row.artifact_id !== data.artifact_id).map((row) => ({ label: row.artifact_id, detail: `${ORIGIN_LABELS[row.origin] ?? row.origin}, ${row.artifact_version}` })) },
    ],
    summary: data.open_total === 0 ? 'Aucune tâche en cours n’est touchée : la nouvelle version s’applique aux prochaines tâches.' : `${data.open_total} tâche(s) en cours garderont leur version ; la nouvelle version s’applique aux prochaines.`,
  };
  if (data.change !== undefined) report.change = data.change;
  return report;
}

export const templateTitle = (item: { label?: Record<string, string> | undefined; artifact_id: string }): string => text(item.label, 'fr', item.artifact_id);
